import { t } from "@hakili/i18n";
import { AlertTriangle } from "lucide-react";
import type { ReactElement } from "react";

/**
 * `ARC-3` (D2 `ui.js` `head()`, `components.css` `.medal`, `.medal.prov`, `.medal.inc`): the dossier head's grade medal.
 * A note: the letter in its tile, « Note de solidité » and the mean. A provisional note: the letter in a dashed white tile and
 * « Note provisoire ». An incomplete note: NO letter and no mean, whatever the cause, « Note incomplète » over « Capacité non calculée ».
 * The letter is the file's (her acts re-derive it); the mean is the run's, printed only when it belongs to that same letter. Where the
 * stage does not serve the analysis, the letter stands alone: a mean the screen could not read is never drawn.
 */
export type MedalState = "note" | "provisoire" | "incomplete";

const MEAN = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function GradeMedal(props: {
  readonly letter: string | null;
  readonly mean: number | null;
  readonly provisional: boolean;
  readonly incomplete: boolean;
}): ReactElement | null {
  const state: MedalState = props.incomplete ? "incomplete" : props.provisional ? "provisoire" : "note";
  if (state !== "incomplete" && props.letter === null) return null;
  const tile =
    state === "incomplete"
      ? "border-2 border-dashed border-[color:var(--hk-warn)] bg-[color:var(--hk-warn-bg)] text-[color:var(--hk-warn)]"
      : state === "provisoire"
        ? "border-2 border-dashed border-[color:var(--hk-brand)] bg-white text-[color:var(--hk-brand-strong)]"
        : "bg-[color:var(--hk-brand)] text-white";
  return (
    <div
      className="flex shrink-0 items-center gap-2.5 rounded-[14px] border border-[color:var(--hk-line)] bg-[color:var(--hk-surface)] py-2 pl-2 pr-4"
      data-testid="bank-dossier-medaille"
      data-state={state}
    >
      <b
        className={`grid h-[46px] w-[46px] place-items-center rounded-[10px] text-xl font-bold ${tile}`}
        aria-hidden={state === "incomplete" ? "true" : undefined}
      >
        {state === "incomplete" ? (
          <AlertTriangle className="h-[22px] w-[22px]" aria-hidden="true" />
        ) : (
          props.letter
        )}
      </b>
      <span className="text-[length:var(--hk-fs-meta)] leading-tight text-[color:var(--hk-text-2)]">
        {state === "incomplete" ? (
          <>
            {t("bank.dossier.noteIncomplete")}
            <br />
            <code className="text-[length:var(--hk-fs-body)] font-semibold text-[color:var(--hk-warn)]">
              {t("bank.dossier.capaciteNonCalculee")}
            </code>
          </>
        ) : (
          <>
            {state === "provisoire" ? t("bank.dossier.noteProvisoire") : t("bank.dossier.noteDeSolidite")}
            {props.mean === null ? null : (
              <>
                <br />
                <code className="text-lg font-semibold text-[color:var(--hk-text)]">
                  {MEAN.format(props.mean)}
                </code>
              </>
            )}
          </>
        )}
      </span>
    </div>
  );
}
